import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AIPanel from "@/components/AIPanel";
import {
  LayoutDashboard, Target, Landmark, Scale, PieChart, Wallet,
  ReceiptText, Award, Users, Settings as SettingsIcon, Bell, LogOut, Sparkles, Building2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { statusClass } from "@/lib/format";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/goals", label: "Goal Setting", icon: Target },
  { to: "/funds", label: "Fund Arrangements", icon: Landmark },
  { to: "/balance", label: "Balance Sheet", icon: Scale },
  { to: "/expense", label: "Dept / Service Expense", icon: PieChart },
  { to: "/center-pnl", label: "Centre P&L", icon: Building2 },
  { to: "/budgeting", label: "Budgeting", icon: Wallet },
  { to: "/taxation", label: "Taxation", icon: ReceiptText },
  { to: "/schemes", label: "Government Schemes", icon: Award },
  { to: "/team", label: "Team Master List", icon: Users },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function monthOptions() {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 10; i++) {
    out.push(d.toISOString().slice(0, 7));
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" });
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const months = monthOptions();
  const [period, setPeriod] = useState(months[0]);
  const [notes, setNotes] = useState([]);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes, location.pathname]);

  const alertCount = notes.filter((n) => n.level === "alert").length;

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <aside className="w-64 fixed left-0 top-0 h-screen bg-[#0B1120] border-r border-[#334155] z-40 flex flex-col">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-[#334155]">
          <div className="h-10 w-10 rounded-lg bg-[#C9A24B] flex items-center justify-center text-[#0B1120] font-heading font-extrabold text-lg">Y</div>
          <div>
            <div className="font-heading font-bold text-sm tracking-tight leading-none">Youngness Finance</div>
            <div className="label-caps mt-1">by Dr. Awish</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-[#C9A24B]/12 text-[#C9A24B] font-medium border border-[#C9A24B]/25"
                    : "text-slate-400 hover:text-slate-100 hover:bg-[#1E293B] border border-transparent"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px] shrink-0" />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#334155]">
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-400 hover:text-rose-400 hover:bg-rose-600/10 transition-all"
          >
            <LogOut className="h-[18px] w-[18px]" /> Sign out
          </button>
        </div>
      </aside>

      <header className="h-20 ml-64 bg-[#0F172A]/90 backdrop-blur-xl border-b border-[#334155] px-8 flex items-center justify-between sticky top-0 z-30">
        <div>
          <div className="label-caps">Finance Command Centre</div>
          <div className="font-heading text-lg font-semibold">{monthLabel(period)} · Overview</div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="period-selector" className="w-[160px] bg-[#0F172A] border-[#334155] h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-[#334155] text-slate-100">
              {months.map((m) => (
                <SelectItem key={m} value={m} className="focus:bg-[#334155]">{monthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="notification-bell" className="relative h-10 w-10 rounded-md border border-[#334155] bg-[#0F172A] flex items-center justify-center hover:border-[#475569] transition-all">
                <Bell className="h-[18px] w-[18px] text-slate-300" />
                {notes.length > 0 && (
                  <span className={`absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full text-white text-[10px] flex items-center justify-center font-semibold ${alertCount > 0 ? "bg-rose-600" : "bg-amber-500"}`}>
                    {notes.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 bg-[#1E293B] border-[#334155] text-slate-100 p-0">
              <div className="px-4 py-3 border-b border-[#334155] font-heading font-semibold text-sm">Notifications ({notes.length})</div>
              <div className="max-h-80 overflow-y-auto">
                {notes.length === 0 && <div className="px-4 py-6 text-sm text-slate-400 text-center">All clear — nothing needs attention.</div>}
                {notes.map((n, i) => (
                  <div key={i} data-testid="notification-item" className="px-4 py-3 border-b border-[#334155]/60 flex gap-3">
                    <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.level === "alert" ? "bg-rose-500" : "bg-amber-400"}`} />
                    <div>
                      <div className="text-sm font-medium">{n.title}</div>
                      <div className="text-xs text-slate-400">{n.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex items-center gap-3 pl-3 border-l border-[#334155]">
            <Avatar className="h-9 w-9 border border-[#334155]">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#C9A24B] text-[#0B1120] text-xs font-semibold">
                {user?.name?.slice(0, 2)?.toUpperCase() || "FN"}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block">
              <div className="text-sm font-medium leading-none">{user?.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{user?.email}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="ml-64 p-8 min-h-[calc(100vh-5rem)]">
        <Outlet context={{ period, setPeriod, monthLabel, reloadNotes: loadNotes }} />
      </main>

      <AIPanel />
    </div>
  );
}
